/**
 * Token Refresh Provider
 *
 * Client component that automatically refreshes access tokens
 * every 14 minutes to prevent 401 errors.
 */

'use client';

import { useTokenRefresh } from '@/src/shared/lib/hooks/useTokenRefresh';

interface TokenRefreshProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component for automatic token refresh
 *
 * Place this high in your component tree (e.g., in root layout)
 * to enable proactive token refresh for authenticated users.
 *
 * @example
 * ```tsx
 * export default function RootLayout({ children }) {
 *   return (
 *     <TokenRefreshProvider>
 *       {children}
 *     </TokenRefreshProvider>
 *   );
 * }
 * ```
 */
export function TokenRefreshProvider({ children }: TokenRefreshProviderProps) {
  // Enable automatic token refresh
  useTokenRefresh({
    refreshInterval: 14 * 60 * 1000, // 14 minutes
    enabled: true,
  });

  // This provider doesn't render anything, just manages side effects
  return <>{children}</>;
}
